import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { ProviderManager } from 'app/fs/res/common/utils/provider-manager';
import { PublicationDocumentMetadata } from 'app/fs/res/publication/model/publication-document-metadata';
import { PublicationDocumentMetadataService } from 'app/fs/res/publication/service/publication-document-metadata.service';

import { HisUiService, HisBaseGlobalsService, HisNotificationService, HisUploadFile, GenericValue, HisFileUploadService, HisUploadConfiguration, HisPlamComponent } from 'hisng';

@Component( {
    selector: 'res-publication-document-dialog',
    templateUrl: './publication-document-dialog.component.html',
    providers: [ProviderManager.getComponentProvider( PublicationDocumentDialogComponent )],
} )
export class PublicationDocumentDialogComponent implements OnInit {
    @Input() document: PublicationDocumentMetadata;
    @Input() visibility: boolean;
    @Output() visibilityChange: EventEmitter<boolean> = new EventEmitter();
    @Output() documentChange: EventEmitter<PublicationDocumentMetadata> = new EventEmitter();

    internalDocumentHiskeyId = 109;
    hisUploadFiles: HisUploadFile[] = [];
    confKey: string;
    documentLabel = '';
    documentLabelPrefix = '';
    uploadUrl = window.location.href.substring( 0, window.location.href.indexOf( '/a/' ) ) + '/api/v1/cs/sys/filemanagement/uploadFile/publication/publication.id';
    allCategories: GenericValue[] = [];
    filteredCategories: GenericValue[] = [];
    selectedCategory: GenericValue;

    constructor( private hisUiService: HisUiService,
        private hisBaseGlobalsService: HisBaseGlobalsService,
        private notificationService: HisNotificationService,
        private fileUploadService: HisFileUploadService,
        private publicationDocumentMetadataService: PublicationDocumentMetadataService ) {
    }

    ngOnInit() {
        this.confKey = this.publicationDocumentMetadataService.getConfKey();
        this.publicationDocumentMetadataService.getPublicationDocumentCategories( this.hisUploadFiles.length ? this.hisUploadFiles[0].documentMetadataCategoryId : null ).subscribe( categories => {
            if ( this.hisUploadFiles.length ) {
                this.selectedCategory = categories.find( c => c.id === this.hisUploadFiles[0].documentMetadataCategoryId );
            }
            this.allCategories = categories;
            this.filteredCategories = categories;
        } );

        this.fileUploadService.getUploadFieldConfig( this.confKey, null, null ).subscribe( c => {
            const config = new HisUploadConfiguration( c );
            if ( config.getLabel()?.length ) {
                this.documentLabelPrefix = config.getRequired() ? HisPlamComponent.REQUIRED_INDICATOR + ' ' : '';
                this.documentLabel += this.hisUiService.translate( config.getLabel() );
            }
        } );

        if ( this.document.documents?.length ) {
            this.hisUploadFiles = this.document.documents;
            this.document.confirmed = true;
        }
        if ( this.document.embargoDate ) {
            this.document.embargoDate = new Date( this.document.embargoDate );
        }
    }

    public filterCategories( event: { query: string } ) {
        this.filteredCategories = [];
        this.allCategories.forEach( category => {
            if ( category.defaulttext.toLowerCase().indexOf( event.query.toLowerCase() ) === 0 ) {
                this.filteredCategories.push( category );
            }
        } );
    }

    public closeDialog() {
        this.fileUploadService.abortAllOnGoingFeedbackRequests();
        this.visibilityChange.emit( false );
    }

    public applyChanges() {
        this.document.documents = this.hisUploadFiles;
        if ( this.hisUploadFiles.length && this.selectedCategory?.id ) {
            this.document.documents[0].documentMetadataCategoryId = this.selectedCategory.id;
        }
        if ( !this.isPublicationInRepositoryDivVisible() ) {
            this.document.access = null;
            this.document.accessAfterEmbargoDate = null;
            this.document.embargoDate = null;
            this.document.license = null;
        }
        let messages = this.getValidationMessages();
        if ( messages.length !== 0 ) {
            this.notificationService.error(
                messages,
                this.hisUiService.translate( 'global.validationerror' )
            );
        } else {
            this.documentChange.emit( this.document );
            this.closeDialog();
        }
    }

    private getValidationMessages() {
        let messages: any[] = [];
        let inputRequired = this.hisUiService.translate( 'validation.required' );
        let ddKeyRequiredTitle = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.title' );
        let ddKeyRequiredCategory = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.category' );
        let ddKeyRequiredComment = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.comment' );
        let ddKeyRequiredAccess = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.access' );
        let ddKeyRequiredAccessAfterEmbargoDate = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.accessAfterEmbargoDate' );
        let ddKeyRequiredEmbargoDate = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.embargoDate' );
        let ddKeyRequiredLicense = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.license' );
        let ddKeyRequiredConfirmation = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.confirmation' );

        if ( !this.hisUploadFiles?.length ) {
            messages.push( `${this.documentLabel}: ${inputRequired}` );
        }
        if ( this.hisUploadFiles?.length ) {
            if ( ddKeyRequiredCategory?.required && !ddKeyRequiredCategory?.hidden && !this.hisUploadFiles[0].documentMetadataCategoryId ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.field.category' )}: ${inputRequired}` );
            }
            if ( ddKeyRequiredTitle?.required && !ddKeyRequiredTitle?.hidden && !this.document.title ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.field.title' )}: ${inputRequired}` );
            }
            if ( ddKeyRequiredComment?.required && !ddKeyRequiredComment?.hidden && !this.hisUploadFiles[0].comment ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.field.comment' )}: ${inputRequired}` );
            }
            if ( ddKeyRequiredAccess?.required && !ddKeyRequiredAccess?.hidden && !this.document.access && this.isPublicationInRepositoryDivVisible() ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.field.access' )}: ${inputRequired}` );
            }
            if ( ddKeyRequiredAccessAfterEmbargoDate?.required && !ddKeyRequiredAccessAfterEmbargoDate?.hidden && !this.document.accessAfterEmbargoDate && this.isPublicationInRepositoryDivVisible() ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.field.accessAfterEmbargoDate' )}: ${inputRequired}` );
            }
            if ( ddKeyRequiredEmbargoDate?.required && !ddKeyRequiredEmbargoDate?.hidden && !this.document.embargoDate && this.isPublicationInRepositoryDivVisible() ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.field.embargoDate' )}: ${inputRequired}` );
            }
            if ( ddKeyRequiredLicense?.required && !ddKeyRequiredLicense?.hidden && !this.document.license && this.isPublicationInRepositoryDivVisible() ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.field.license' )}: ${inputRequired}` );
            }
            if ( ddKeyRequiredConfirmation?.required && !ddKeyRequiredConfirmation?.hidden && !this.document.confirmed ) {
                messages.push( `${this.hisUiService.translate(
                    'fs.res.publication.publicationDocuments.add.confirmation' )}: ${inputRequired}` );
            }
        }
        return messages;
    }

    addFile() {
        this.notificationService.infoI18n( 'de.his.javang.gefunden.change' );
    }

    removeFile() {
        this.fileUploadService.abortAllOnGoingFeedbackRequests();
        this.hisUploadFiles = [];
        this.document.confirmed = false;
    }

    public isHidden( ddKey: string ) {
        return this.hisBaseGlobalsService.getDd( ddKey )?.hidden;
    }

    public isPublicationInRepositoryDivVisible() {
        let dDEntryAccess = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.access' );
        let dDEntryEmbargoDate = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.embargoDate' );
        let dDEntryAccessAfterEmbargoDate = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.accessAfterEmbargoDate' );
        let dDEntryLicense = this.hisBaseGlobalsService.getDd( 'fs.res.publication.PublicationDocumentMetadata.license' );

        return this.selectedCategory && this.selectedCategory?.hiskeyId !== this.internalDocumentHiskeyId &&
            !( dDEntryAccess?.hidden && dDEntryEmbargoDate?.hidden && dDEntryAccessAfterEmbargoDate?.hidden && dDEntryLicense?.hidden );
    }

    public onClear( field: string ) {
        switch ( field ) {
            case 'access': this.document.access = null; break;
            case 'accessAfterEmbargoDate': this.document.accessAfterEmbargoDate = null; break;
            case 'license': this.document.license = null; break;
            default: console.log( 'ERROR: Unsupported field:', field );
        }
    }
}
